import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useStoreOwner } from '../store/useStoreOwner'
import { useStaffAuth } from '../store/useStaffAuth'
import { useStoreDeviceApprovalGate, recheckStoreDeviceApproval } from '../hooks/useStoreDeviceApprovalGate'
import { useStoreDevicePendingApprovals } from '../hooks/useStoreDevicePendingApprovals'
import { hasTotpFactor } from '../lib/totpMfa'
import { auth } from '../lib/firebase'
import { api } from '../services/api'
import StoreDeviceApprovalWaitingModal from './StoreDeviceApprovalWaitingModal'
import StoreDeviceApprovalPromptModal from './StoreDeviceApprovalPromptModal'
import StoreTermsGate from './StoreTermsGate'
import EcVendorTermsGate from './EcVendorTermsGate'
import StoreSuspensionBanner from './StoreSuspensionBanner'
import StoreSaasNoticeBanner from './StoreSaasNoticeBanner'
import StoreTotpRequiredGate from './StoreTotpRequiredGate'

/**
 * 店舗オーナー認証 gate + 初見デバイス承認 gate (#867 v3+).
 *
 * 顧客側 `RequireAuth` (#819) と同様の構造で、店舗オーナー専用に動作:
 * 1. 未ログイン → /store/login
 * 2. ログイン済 + **2FA enrolled (TOTP or WebAuthn)** → device-approval gate を評価
 *    - 否認済端末 → waiting modal (denied state)
 *    - 未信頼端末 → waiting modal (passkey 認証で即解除可)
 *    - trusted 端末 + 承認待ち pending あり → prompt modal (triage 強制)
 * 3. 2FA 未設定 owner は gate スキップ(opt-in 設計・離脱回避優先で
 *    本人が設定するか判断する。memory: ユーザー離脱が怖いので opt-in)
 *
 * `/store/settings/devices` (端末管理画面) では gate を skip
 * (revoke 等の操作で「最後の trusted を消すと自分が出られない」状況を
 * UI で扱うため、gate で塞ぐと到達不能になる)。
 */
/**
 * 店舗画面の CSP は document の URL で決まる (nginx の map。/store/ と /staff/ だけ USB 橋渡しの
 * localhost:8443 を許す)。`/` などから画面内遷移で店舗画面に入ると、厳しい方の CSP のままで
 * 厨房の印刷が必ず失敗する。そのときは一度だけ読み直して、店舗用の CSP で開き直す。
 * (繰り返さないよう、試みは 1 セッション 1 回)
 */
function reloadIfDocumentIsNotStore(): void {
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined
    const docPath = nav ? new URL(nav.name).pathname : window.location.pathname
    if (/^\/(store|staff)\//.test(docPath)) return
    if (sessionStorage.getItem('store-csp-reload') === '1') return
    sessionStorage.setItem('store-csp-reload', '1')
    window.location.reload()
  } catch {
    // 古いブラウザ・プライベートモード: 何もしない
  }
}

export default function RequireStoreAuth({ children }: { children: React.ReactNode }) {
  useEffect(reloadIfDocumentIsNotStore, [])
  const { isStoreLoggedIn, loading, firebaseUser } = useStoreOwner()
  const staffSession = useStaffAuth((s) => s.session)
  const location = useLocation()

  // WebAuthn enrolled 判定 (TOTP は hasTotpFactor で同期取得、WebAuthn は API fetch)。
  // uid と一緒に保存して、別 user 切替時の stale 表示を derived value で回避
  // (effect body での synchronous setState を避け react-hooks/set-state-in-effect
  // を踏まない)。
  const [enrolledRecord, setEnrolledRecord] = useState<{ uid: string; value: boolean } | null>(null)
  useEffect(() => {
    if (!isStoreLoggedIn || !firebaseUser?.uid) return
    const targetUid = firebaseUser.uid
    let cancelled = false
    api.listStoreWebauthnCredentials()
      .then((r) => {
        if (!cancelled) setEnrolledRecord({ uid: targetUid, value: (r?.credentials?.length ?? 0) > 0 })
      })
      .catch(() => {
        if (!cancelled) setEnrolledRecord({ uid: targetUid, value: false })
      })
    return () => {
      cancelled = true
    }
  }, [isStoreLoggedIn, firebaseUser?.uid])

  // logout / user 切替時は uid mismatch で null 化(stale 値で gate 誤発動を回避)。
  const webauthnEnrolled: boolean | null =
    enrolledRecord && firebaseUser?.uid && enrolledRecord.uid === firebaseUser.uid
      ? enrolledRecord.value
      : null

  const totpEnrolled = hasTotpFactor(firebaseUser ?? auth.currentUser)
  const has2FA = totpEnrolled || webauthnEnrolled === true

  const {
    requiresApproval,
    trusted: approvalTrusted,
    denied: approvalDenied,
    loading: approvalLoading,
  } = useStoreDeviceApprovalGate(has2FA)
  const { pending: devicePending, refresh: refreshPending } =
    useStoreDevicePendingApprovals(has2FA && approvalTrusted)

  // #995 スタッフモード: staff セッションがあれば owner の 2FA/端末承認 gate 対象外で
  // 通す（staff は Firebase ユーザーを持たない）。owner に戻るには staff ログアウト or
  // /store/login（どちらも staff セッションをクリアする）。
  if (staffSession) {
    return <>{children}</>
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-warm-white">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-navy border-t-transparent" />
      </div>
    )
  }

  if (!isStoreLoggedIn) {
    // **どこへ行こうとしたかを渡す。** メールのリンクから来た人は、ログイン後に
    // ダッシュボードへ飛ばされると目的の画面 (催告なら支払い) に自力で辿り着けない。
    // 催告メールが催告として機能しなくなる (#1689)。
    // 問い合わせの部分 (`?vendor=` など) も持っていく (#2612 EC のご注文のメールのリンクは、どのお店の分かを付ける)
    return <Navigate to="/store/login" state={{ from: location.pathname + location.search }} replace />
  }

  // /store/settings/devices は gate 対象外(revoke 操作の到達性確保)。
  const isDeviceSettings = location.pathname === '/store/settings/devices'

  // #2663 TOTP の必須化 (STORE_TOTP_MODE=required_all) は、端末承認・規約より先に見る。
  // TOTP の無いオーナーは登録の画面で塞がれ、ほかの画面には入れない
  return <StoreTotpRequiredGate>{gated()}</StoreTotpRequiredGate>

  function gated() {
    if (!isDeviceSettings && has2FA) {
      if (approvalDenied) {
        return (
          <StoreDeviceApprovalWaitingModal
            open
            denied
            onRecheck={() => recheckStoreDeviceApproval()}
          />
        )
      }
      if (requiresApproval && !approvalLoading) {
        return (
          <StoreDeviceApprovalWaitingModal
            open
            denied={false}
            onRecheck={() => recheckStoreDeviceApproval()}
          />
        )
      }
      if (approvalTrusted && devicePending.length > 0) {
        return (
          <StoreDeviceApprovalPromptModal
            open
            pending={devicePending}
            onChanged={() => refreshPending()}
          />
        )
      }
    }

    // #1657 端末承認まで通ったら、最後に加盟店規約の同意を見る。
    // ここに置くのは、店舗オーナーのページ全部を 1 箇所で覆えるため
    // (RequireApprovedOwner は /store/onboarding に掛かっておらず取りこぼす)。
    // #1683 休会 (SaaS 利用料の未払い) の告知。**塞がずに知らせる** — 休会中に
    // オーナーがやるべき支払いと予約の後始末はどちらもこの中にあるので、
    // モーダルで覆うと取り立てが自分で遠のく。
    return (
      <StoreTermsGate>
        {/* #1710 EC 出店規約の同意。EC の出店者としてリンクされた店舗を持つオーナーにだけ
          * 出る。加盟店規約と違い**閉じられる** — EC の取引条件なので、未同意でも PAY の
          * レジや注文は止めない (同意まで EC 連携の操作が出ないだけ)。 */}
        <EcVendorTermsGate />
        <StoreSuspensionBanner />
        {/* #1689 SaaS 化の告知と承認のお願い。休会バナーと同じく**塞がない** —
          * オーナーにやってほしいこと (申込み・カード登録) が画面の中にあるので、
          * その画面を覆ってはいけない。 */}
        <StoreSaasNoticeBanner />
        {children}
      </StoreTermsGate>
    )
  }
}
